import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { SketchPicker } from "react-color";
import { DEFAULT_PDF_COLORS } from "../../layouts/PdfSettingsContext.js";

const ReportPdfColorModal = ({
  open,
  onClose,

  // PDF Background Color
  themeColor: themeColorProp,
  setThemeColor: setThemeColorProp,
  tempThemeColor,
  onThemeChange,

  // PDF Text Color
  textColor: textColorProp,
  setTextColor: setTextColorProp,
  tempTextColor,
  onTextChange,

  reportTitle = "Report Title",
  loading = false,
  disabled = false,
  onGenerate,
  onPrint,
}) => {
  const theme = useTheme();

  /*
   * IMPORTANT:
   * themeColor here means PDF BACKGROUND COLOR.
   *
   * It is NOT the application's global Theme Color.
   */
  const activePdfBackgroundColor =
    themeColorProp ||
    tempThemeColor ||
    DEFAULT_PDF_COLORS.primary;

  const activePdfTextColor =
    textColorProp ||
    tempTextColor ||
    DEFAULT_PDF_COLORS.bodyText;

  const setPdfBackgroundColor =
    setThemeColorProp || onThemeChange;

  const setPdfTextColor =
    setTextColorProp || onTextChange;

  const handleGenerate = async () => {
    if (onGenerate) {
      await onGenerate(
        activePdfBackgroundColor,
        activePdfTextColor
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(15, 23, 42, 0.72)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94%", sm: 620, md: 700 },
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: theme.shadows[20],
          overflow: "hidden",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            bgcolor:
              activePdfBackgroundColor ||
              DEFAULT_PDF_COLORS.primary,
            px: 2.5,
            py: 1.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 3,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "1.05rem",
              }}
            >
              Customize PDF Colors
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.85)",
              }}
            >
              Customize the PDF background and text colors.
            </Typography>
          </Box>

          <IconButton
            aria-label="Close color customization"
            onClick={onClose}
            sx={{
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.10)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* CONTENT */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            maxHeight: "60vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                md: "row",
              },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            {/* PDF BACKGROUND COLOR */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                PDF Background
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    backgroundColor: activePdfBackgroundColor,
                    border: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontFamily: "monospace",
                  }}
                >
                  {activePdfBackgroundColor.toUpperCase()}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  "& .sketch-picker": {
                    width: "100% !important",
                    boxSizing: "border-box",
                    backgroundColor:
                      theme.palette.background.paper,
                    boxShadow: "none !important",
                  },
                }}
              >
                <SketchPicker
                  color={activePdfBackgroundColor}
                  onChangeComplete={(nextColor) =>
                    setPdfBackgroundColor(nextColor.hex)
                  }
                />
              </Box>
            </Box>

            {/* PDF TEXT COLOR */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                PDF Text Color
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    backgroundColor: activePdfTextColor,
                    border: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontFamily: "monospace",
                  }}
                >
                  {activePdfTextColor.toUpperCase()}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  "& .sketch-picker": {
                    width: "100% !important",
                    boxSizing: "border-box",
                    backgroundColor:
                      theme.palette.background.paper,
                    boxShadow: "none !important",
                  },
                }}
              >
                <SketchPicker
                  color={activePdfTextColor}
                  onChangeComplete={(nextColor) =>
                    setPdfTextColor(nextColor.hex)
                  }
                />
              </Box>
            </Box>
          </Box>

          {/* PREVIEW */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.default",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 700,
              }}
            >
              PDF Preview
            </Typography>

            <Box
              sx={{
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* PDF HEADER / BACKGROUND */}
              <Box
                sx={{
                  backgroundColor:
                    activePdfBackgroundColor,
                  px: 2,
                  py: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "0.95rem",
                  }}
                >
                  Organization Name
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "0.75rem",
                  }}
                >
                  Branch Name
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.72rem",
                  }}
                >
                  {reportTitle}
                </Typography>
              </Box>

              {/* PDF BODY */}
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: "background.paper",
                }}
              >
                <Typography
                  sx={{
                    color: activePdfTextColor,
                    fontSize: "0.75rem",
                  }}
                >
                  Sample report text and table content
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            justifyContent: "flex-end",
            gap: 1,
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setPdfBackgroundColor(
                DEFAULT_PDF_COLORS.primary
              );

              setPdfTextColor(
                DEFAULT_PDF_COLORS.bodyText
              );
            }}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Reset
          </Button>

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          {onPrint && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              disabled={loading || disabled}
              onClick={() =>
                onPrint(
                  activePdfBackgroundColor,
                  activePdfTextColor
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 2,
              }}
            >
              Print
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress
                  size={17}
                  color="inherit"
                />
              ) : (
                <PictureAsPdfIcon />
              )
            }
            disabled={loading || disabled}
            onClick={handleGenerate}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
            }}
          >
            {loading
              ? "Generating..."
              : "Generate PDF"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReportPdfColorModal;